import React, { createContext, useContext, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DICTIONARIES, type Dict } from "@/i18n/dictionary";
import {
  DEFAULT_LOCALE,
  LOCALES,
  type LocaleCode,
  buildPath,
  type RouteKey,
} from "@/lib/routes";

interface I18nCtx {
  locale: LocaleCode;
  otherLocale: LocaleCode;
  t: Dict;
  path: (key: RouteKey) => string;
  switchLocale: () => void;
}

const Ctx = createContext<I18nCtx | null>(null);

function isLocale(v: string | undefined): v is LocaleCode {
  return !!v && (LOCALES as string[]).includes(v);
}

export function useLocale(): LocaleCode {
  const { locale } = useParams();
  return isLocale(locale) ? locale : DEFAULT_LOCALE;
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const locale = useLocale();
  const location = useLocation();
  const navigate = useNavigate();

  const value = useMemo<I18nCtx>(() => {
    const other: LocaleCode = locale === "en" ? "it" : "en";
    return {
      locale,
      otherLocale: other,
      t: DICTIONARIES[locale],
      path: (key: RouteKey) => buildPath(key, locale),
      switchLocale: () => {
        const rest = location.pathname.replace(/^\/(en|it)/, "");
        navigate(`/${other}${rest}${location.search}`, { replace: false });
      },
    };
  }, [locale, location.pathname, location.search, navigate]);

  React.useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useT(): I18nCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useT must be used within <I18nProvider>");
  return v;
}
